/*
 * Copyright 2018 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import Dot from '../gfx/Dot';
import Pill from '../gfx/Pill';
import Label from '../gfx/Label';

import TWEEN from '@tweenjs/tween.js';

import Proximity from '../utils/Proximity';

import { font16, font24, font32, font48, font64 } from '../fonts/fonts';

import * as Constants from '../constants/Constants';

let tempWorldPos = new THREE.Vector3();
let tempCameraPos = new THREE.Vector3();
let scratchVector3 = new THREE.Vector3();
let deltaVector3 = new THREE.Vector3();
let scratchMatrix4 = new THREE.Matrix4();

export default class ARAnnotation extends THREE.Object3D {
  constructor(props) {
    props = props ? props : {};
    props = Object.assign(
      {
        size: 64,
        camera: undefined,
        visibilityThreshold: 0.25,
        color: new THREE.Color(0xff0000),
        label: {
          scale: 0.00175,
          text: 'AR Button Label',
          color: new THREE.Color(0xff0000),
          alpha: 0.75,
          font: font32,
          padding: 0,
        },
        pill: {
          color: new THREE.Color(0xff00ff),
          alpha: 0.75,
        },
        dot: {
          size: 64,
          color: new THREE.Color(0xff00ff),
          alpha: 0.75,
        },
      },
      props
    );
    super(props);
    this.setup(props);
  }

  setup = props => {
    this.setupProperties(props);
    this.setupTargetMesh(props);
    this.setupDot(props);
    this.setupProximity(props);
    this.setupPillLabel(props);
    this.setupHitMesh(props);
    this.setupBehavior();
    this.fadeOut(0);
  };

  setupProperties = props => {
    this.upAngle = 0.0;
    this.camera = props.camera;
    this.deviceRotation = undefined;
    this.visibilityThreshold = props.visibilityThreshold;
    this.name = 'ARButton';
    this.scale.set(0.0001, 0.0001, 0.0001);
    this.hitScalar = props.hitScalar != undefined ? props.hitScalar : 1.0;
    this.scale.set(0.0001, 0.0001, 0.0001);
    this.faded = true;
    this.threshold = props.threshold ? props.threshold : 1.25;
    this.color = {
      value: props.color != undefined ? props.color : new THREE.Color(0xffffff),
    };
    this.renderOrder = props.renderOrder;
    this.size = typeof props.size == 'number' ? props.size : 1.0;
    this.normal = props.normal == undefined
      ? new THREE.Vector3(0, 0, 1)
      : props.normal;
  };

  setupTargetMesh = props => {
    this.targetMesh = new THREE.Mesh(
      new THREE.PlaneBufferGeometry(0.1, 0.1, 2, 2),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xffff00),
        transparent: true,
        depthTest: false,
        depthWrite: false,
        opacity: 0.0,
        side: THREE.DoubleSide,
      })
    );
    this.add(this.targetMesh);

    this.targetMesh.onBeforeRender = this.update;
  };

  setupHitMesh = props => {
    let layout = this.label.getLayout();
    let size = props.size;
    let padding = (size - layout._capHeight) / 1.667 + props.label.padding;
    let width = size * 2.5 + layout.width + padding * 2;
    let height = size;

    this.hitMesh = new THREE.Mesh(
      new THREE.PlaneBufferGeometry(width, props.size * 2, 2, 2),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(0xff0000),
        transparent: true,
        depthTest: false,
        depthWrite: false,
        opacity: 0.0,
        side: THREE.DoubleSide,
      })
    );

    this.hitMesh.position.set(width * 0.5 - size * 1.0, 0, 0);

    this.dot.add(this.hitMesh);
  };

  setupProximity = props => {
    this.proximity = new Proximity({ object: this, threshold: 0.25 });
  };

  setupPillLabel = props => {
    this.setupLabel(props.label);

    let layout = this.label.getLayout();
    let size = props.size;
    let padding = (size - layout._capHeight) / 1.667 + props.label.padding;
    let width = layout.width + padding * 2;
    let height = size;

    this.setupPill(
      Object.assign(
        {
          width: width,
          height: height,
        },
        props.pill
      )
    );

    let pillPlacer = new THREE.Object3D();
    pillPlacer.position.set(size + width / 2, 0, 0);
    pillPlacer.add(this.pill);

    let placer = new THREE.Object3D();
    let offset = new THREE.Vector3();

    placer.scale.set(1.0, -1.0, 1.0);
    placer.position.set(-layout.width / 2.0, -layout._capHeight / 2.0, 0);
    placer.add(this.label);

    pillPlacer.add(placer);
    this.dot.add(pillPlacer);
  };

  setupDot = props => {
    this.dot = new Dot(props.dot);
    this.dot.addEventListener('click', event => {
      this.dispatchEvent({ type: 'click', object: this });
    });
  };

  setupPill = props => {
    this.pill = new Pill(props);
  };

  setupLabel = props => {
    this.label = new Label(props);
  };

  setupBehavior = () => {
    this.proximity.addEventListener('thresholdEntered', event => {
      if (!this.faded) {
        if (this.proximity.distance <= this.threshold) {
          this.dot.fadeIn(Constants.duration);
          this.pill.fadeIn(Constants.duration);
          this.label.fadeIn(Constants.duration);
        }
        if (this.proximity.distance <= this.threshold * 2.0) {
          this.dot.fadeIn(Constants.duration);
        }
      }
    });

    this.proximity.addEventListener('thresholdExited', event => {
      if (this.proximity.distance >= this.threshold) {
        this.pill.fadeOut(Constants.duration);
        this.label.fadeOut(Constants.duration);
      }
      if (this.proximity.distance >= this.threshold * 2.0) {
        this.dot.fadeOut(Constants.duration);
      }
    });

    this.addEventListener('visible', event => {
      if (this.proximity.distance <= this.threshold) {
        this.pill.fadeIn(Constants.duration);
        this.label.fadeIn(Constants.duration);
      }
      if (this.proximity.distance <= this.threshold * 2.0) {
        this.dot.fadeIn(Constants.duration);
      }
    });

    this.addEventListener('invisible', event => {
      this.dot.fadeOut(Constants.duration);
      this.pill.fadeOut(Constants.duration);
      this.label.fadeOut(Constants.duration);
    });
  };

  getProximity = () => {
    return this.proximity;
  };

  update = (renderer, scene, camera, geometry, material, group) => {
    this.dot.scale.copy(this.scale);
    this.dot.updateMatrixWorld();
    this.pill.scale.copy(this.scale);
    this.pill.updateMatrixWorld();
    this.label.scale.copy(this.scale);
    this.label.updateMatrixWorld();

    camera.getWorldPosition(tempCameraPos);
    this.getWorldPosition(tempWorldPos);

    scratchVector3.copy(this.normal);
    scratchMatrix4.extractRotation(this.matrixWorld);
    scratchVector3.applyMatrix4(scratchMatrix4);
    scratchVector3.normalize();

    deltaVector3.copy(tempCameraPos);
    deltaVector3.sub(tempWorldPos);
    deltaVector3.normalize();

    let dot = scratchVector3.dot(deltaVector3);

    if (dot < this.visibilityThreshold) {
      this.faded = true;
    } else {
      this.faded = false;
    }

    if (this.faded != this.lastFaded) {
      if (this.faded) {
        this.dispatchEvent({ type: 'invisible', object: this });
        this.fadeOut(Constants.duration);
      } else {
        this.dispatchEvent({ type: 'visible', object: this });
        this.fadeIn(Constants.duration);
      }
    }

    this.lastFaded = this.faded;

    let size = renderer.getDrawingBufferSize();
    let halfWidth = size.width / 2.0;
    let halfHeight = size.height / 2.0;

    tempWorldPos.project(camera);
    tempWorldPos.x = tempWorldPos.x * halfWidth + halfWidth;
    tempWorldPos.y = tempWorldPos.y * halfHeight + halfHeight;
    tempWorldPos.z = 0;

    this.dot.position.copy(tempWorldPos);
    this.dot.updateMatrixWorld(true);
    renderer.render(this.dot, this.camera);
  };

  fadeIn = (duration, delay, cb) => {
    this.dot.fadeIn(duration, delay);
    this.pill.fadeIn(duration, delay);
    this.label.fadeIn(duration, delay, cb);
  };

  fadeOut = (duration, delay, cb) => {
    this.dot.fadeOut(duration, delay);
    this.pill.fadeOut(duration, delay);
    this.label.fadeOut(duration, delay, cb);
  };

  getHitMesh = () => {
    return this.dot;
  };
}



// WEBPACK FOOTER //
// ./src/ar/ARAnnotation.js